import { toaster } from "@/components/ui/toaster";
import { formatUtcDatetimeToColombian } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Portal,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { formatDocument } from "../../screenings/columns";
import { useProtectTRPC } from "../../trpc";
import { formatPeriods } from "../columns";
import { Claim } from "../types";
import { useInvalidateClaims } from "../useInvalidateClaims";
import StatusBadge from "./StatusBadge";

type Props = {
  open: boolean;
  onClose: () => void;
  claim?: Claim;
  confirmDismiss?: boolean;
};

const currency = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
});

const buildDetails = (claim: Claim) => [
  { label: "Inmueble", value: claim.propertyAddress || "-" },
  { label: "Meses reclamados", value: formatPeriods(claim.reportedPeriods) },
  { label: "Canon mensual", value: currency.format(claim.monthlyRent) },
  ...(claim.adminFee !== undefined
    ? [{ label: "Administración", value: currency.format(claim.adminFee) }]
    : []),
  { label: "Valor reclamado", value: currency.format(claim.amount) },
  { label: "Estado", value: <StatusBadge status={claim.status} /> },
  {
    label: "Reclamada",
    value: formatUtcDatetimeToColombian(claim.createdAt),
  },
  ...(claim.dismissedAt
    ? [
        {
          label: "Desistida",
          value: formatUtcDatetimeToColombian(claim.dismissedAt),
        },
      ]
    : []),
];

const ClaimDetailModal = ({
  open,
  onClose,
  claim,
  confirmDismiss = false,
}: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const invalidateClaims = useInvalidateClaims();
  const [isConfirming, setIsConfirming] = useState(confirmDismiss);

  useEffect(() => {
    setIsConfirming(confirmDismiss);
  }, [open, claim?.id, confirmDismiss]);

  const { mutate: dismissClaim, isPending } = useMutation({
    ...trpc.claims.dismiss.mutationOptions(),
    onSuccess: async () => {
      await invalidateClaims();
      toaster.create({
        title: "Reclamación desistida",
        description: "El contrato puede volver a reclamarse.",
        type: "success",
        duration: 6000,
        meta: { closable: true },
      });
      onClose();
    },
    onError: () =>
      toaster.create({
        title: "Error",
        description: "Hubo un error al desistir la reclamación.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      }),
  });

  const handleDismiss = () => {
    if (!merchant || !claim) return;

    dismissClaim({ rentalsMerchantId: merchant.id, id: claim.id });
  };

  const details = claim ? buildDetails(claim) : [];
  const canDismiss = claim?.status === "reported";

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => !e.open && !isPending && onClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            {claim && (
              <>
                <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
                  <Dialog.Title>
                    <Heading>{claim.tenantName}</Heading>
                    <Text textStyle="sm" color="gray.500" fontWeight="normal">
                      {formatDocument(claim)} · Contrato {claim.contractNumber}
                    </Text>
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.CloseTrigger asChild>
                  <CloseButton marginTop="1" disabled={isPending} />
                </Dialog.CloseTrigger>
                <Dialog.Body py={6}>
                  <VStack gap={4} align="stretch">
                    {details.map((detail, index) => (
                      <Box key={detail.label}>
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight={600} color="gray.600">
                            {detail.label}:
                          </Text>
                          <Text color="gray.700">{detail.value}</Text>
                        </HStack>
                        {index < details.length - 1 && (
                          <Separator borderColor="gray.200" />
                        )}
                      </Box>
                    ))}
                    {isConfirming && (
                      <Box bg="#FFF7D6" borderRadius="md" p={3}>
                        <Text fontSize="sm" color="gray.700">
                          Vas a desistir de esta reclamación. El contrato podrá
                          reclamarse de nuevo.
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer borderTop="1px solid #ebecef80">
                  {isConfirming ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setIsConfirming(false)}
                        disabled={isPending}
                      >
                        Volver
                      </Button>
                      <Button
                        colorPalette="red"
                        onClick={handleDismiss}
                        loading={isPending}
                      >
                        Sí, desistir
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={onClose}>
                        Cerrar
                      </Button>
                      {canDismiss && (
                        <Button
                          variant="outline"
                          colorPalette="red"
                          onClick={() => setIsConfirming(true)}
                        >
                          Desistir
                        </Button>
                      )}
                    </>
                  )}
                </Dialog.Footer>
              </>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ClaimDetailModal;
